<template>
  <div
    v-if="carousel"
    ref="carouselContRef"
    :class="['carousel-container', globalClassAttributes]"
    :style="styleAttributes"
  >
    <div
      v-if="isHeadingAvailable"
      class="carousel-heading margin-bottom-m"
      :style="gridStyle"
    >
      <div
        v-if="carousel?.attributes?.heading"
        class="heading"
        v-html="sanitize(carousel?.attributes?.heading, cleanStyles)"
      />
      <div
        v-if="carousel?.attributes?.subHeading"
        class="sub-heading margin-top-xxs"
        v-html="sanitize(carousel?.attributes?.subHeading, cleanStyles)"
      />
      <a
        v-if="carousel?.ctas?.children?.[0]"
        class="heading-cta link-med align-self-bottom"
        :href="carousel?.ctas?.children[0]?.url"
        :style="setMarginLeft"
      >
        {{ carousel?.ctas?.children[0]?.name }}
      </a>
    </div>
    <Carousel
      v-if="carousel?.cards?.length && parseInt(carousel?.attributes?.colCount, 10) >= 4"
      ref="stdCarousel"
      :list="carousel?.cards"
      :ssr-size="getSSRSize()"
      :number-of-items="numItems"
      :show-skeleton="true"
      :skeleton-height="`${carousel?.cards[0]?.imageMap?.height || carousel?.cards[0]?.banner?.height}px`"
      :max-items-lazy="carousel?.cards?.length"
      :gutter="{
        large: 24,
        medium: 16,
        small: 16
      }"
      @carousel-next="nextClick"
      @carousel-prev="prevClick"
    >
      <template #item="slotProps">
        <CarouselCard
          v-if="slotProps?.data"
          :card="slotProps?.data"
        />
      </template>
    </Carousel>
  </div>
</template>

<script setup>
import {
  ref, onMounted, computed,
} from 'vue';
import Carousel from '@atomic-ui/carousel';
import { sanitize } from '@vue-util/sanitize';
import { useResizeObserver } from '@vueuse/core';
import { brand } from 'config';
import CarouselCard from './CarouselCard';
import { getClassAttributes, getStyleAttributes } from '../../helpers/getAttributes';
import cleanStyles from '../../helpers/getCleanStyles';

const props = defineProps({
  carousel: {
    type: Object,
    default: () => {},
  },
  meta: {
    type: Object,
    default: () => {},
  },
});

const carouselContRef = ref();
const stdCarousel = ref();
const top = ref();
const nextClick = () => {};
const prevClick = () => {};

const globalClassAttributes = getClassAttributes(props?.carousel?.attributes);

const styleAttributes = ref({});
onMounted(() => {
  if (props?.carousel?.attributes) {
    styleAttributes.value = getStyleAttributes(props.carousel.attributes);
  }
});

useResizeObserver(carouselContRef, () => {
  const prodImgHeight = carouselContRef.value.querySelector('.image-container').offsetHeight;
  const prevBtn = stdCarousel.value.$el.querySelector('.v-button-wrapper.prev');
  const nextBtn = stdCarousel.value.$el.querySelector('.v-button-wrapper.next');
  const btnHeight = (prevBtn?.style.display === 'none') ? nextBtn?.offsetHeight : prevBtn?.offsetHeight;
  top.value = `${(prodImgHeight - btnHeight) / 2}px`;
});

const isHeadingAvailable = computed(() => props.carousel?.attributes?.heading
  || props.carousel?.attributes?.subHeading);
const numItems = computed(() => {
  let large;
  if (brand === 'bcom') {
    large = 4;
  } else {
    large = Number(props?.carousel?.attributes?.colCount) > 5 ? 5.2 : Number(props?.carousel?.attributes?.colCount);
  }
  return {
    large,
    medium: 2.8175,
    small: 1.2912,
  };
});

const windowSizeByDeviceType = {
  desktop: 'large',
  tablet: 'medium',
  phone: 'small',
};

const getSSRSize = () => (props.meta ? windowSizeByDeviceType[props.meta?.context?.deviceType?.toLowerCase()] : null);
const getWindowSize = () => {
  if (typeof window === 'undefined') {
    return getSSRSize();
  }
  if (window.innerWidth < 600) {
    return 'small';
  }
  if (window.innerWidth > 600 && window.innerWidth < 1024) {
    return 'medium';
  }
  return 'large';
};
const windowSize = ref(getWindowSize());

const gridStyle = computed(() => ({
  display: 'grid',
  gridTemplateColumns: '1fr auto',
  alignItems: 'baseline',
}));
const setMarginLeft = computed(() => (
  windowSize.value === 'small' ? { marginLeft: '16px', alignSelf: 'baseline' } : {}
));
</script>

<style lang="scss" scoped>
.carousel-heading {
  @include breakpoint(medium) {
    grid-template-areas:
      "heading heading-cta"
      "sub-heading sub-heading";
    .heading {
      grid-area: heading;
    }
    .sub-heading {
      grid-area: sub-heading;
    }
    .heading-cta {
      grid-area: heading-cta;
    }
  }
}

:deep(.v-button-wrapper) {
  height: rem-calc(32);
  top: v-bind(top);
  bottom: auto;
}
</style>

<style lang="scss" brand="mcom" scoped>
@import "./../../scss/globalAttributes.mcom.scss";

:deep(.text-block .subHeading) {
  margin-top: rem-calc(8px);
}
</style>

<style lang="scss" brand="bcom" scoped>
@import "./../../scss/globalAttributes.bcom.scss";
</style>
